import styled from '@emotion/styled';
import { Col } from 'antd';
import React from 'react';
import { connect } from 'react-redux';

import { MusicPlayer } from 'src/modules/meditations/components/MusicPlayer';
import { redirectToUrl } from 'src/modules/navigation/actions/navigationActions';
import { color, font, mediaQueries, shadow } from 'src/shared/styles/variables';

import { setRelaxationRecordingListened } from '../actions/pfaProgramActions';
import { RelaxationInstructions } from './RelaxationInstructions';

const BoxContainer = styled(Col)({
    paddingBottom: '1rem',
});

const BoxWrapper = styled<any>(Col)({
    backgroundColor: color.white,
    borderRadius: '0.5rem',
    margin: '0 1rem',
    display: 'flex',
    boxShadow: shadow.lightCardShadow,
    height: '100%',
    minHeight: 315,
    [mediaQueries.sm]: {
        flexDirection: 'column',
    },
});

const ImageWrapper = styled(Col)({
    position: 'relative',
    backgroundColor: color.polar,
    padding: '2rem',
    display: 'flex',
    alignItems: 'center',
});

const InstructionsWrapper = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    padding: '1.5rem 1rem',
});

const InstructionsHeading = styled('h3')(
    {
        color: color.blueLagoon,
        margin: 0,
    },
    font.heavy(font.size.mediumExtended),
);

const Link = styled('a')(
    {
        cursor: 'pointer',
        color: color.baliHai,
        '&:hover': {
            textDecoration: 'underline',
            color: color.blueLagoon,
        },
    },
    font.medium(font.size.base),
);

interface StyledIconProps {
    iconUrl: string;
}

const StyledIcon = styled('i')<StyledIconProps>(({ iconUrl }) => ({
    display: 'block',
    backgroundImage: `url('${iconUrl}')`,
    maxWidth: '10rem',
    width: '100%',
    height: '7.5rem',
    margin: '0 auto',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
}));

const MusicPlayerWrapper = styled<any>(Col)({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem 0 1rem',
    [mediaQueries.sm]: {
        padding: '1rem 0 3rem',
    },
});

const Title = styled('h4')(
    {
        color: color.blueLagoon,
        marginTop: '1rem',
    },
    font.heavy(font.size.mediumExtended),
);

interface ActionsProps {
    redirectToUrl: typeof redirectToUrl;
    setRelaxationRecordingListened: typeof setRelaxationRecordingListened;
}

interface Props extends ActionsProps {
    title: string;
    icon: string;
    track: string;
    instructions?: React.ReactNode;
    showMeditationsLink?: boolean;
}

interface State {
    areInstructionsOpen: boolean;
}

class RelaxationStrategiesBoxContainer extends React.Component<Props, State> {
    public state: State = {
        areInstructionsOpen: false,
    };

    private handleTrackFinished = () => {
        this.props.setRelaxationRecordingListened();
    };

    private goToMeditations = () => {
        this.props.redirectToUrl('/dashboard/meditations');
    };

    private handleToggleInstructions = (): void => {
        this.setState({ areInstructionsOpen: !this.state.areInstructionsOpen });
    };

    public render(): JSX.Element {
        const { title, icon, track, instructions } = this.props;

        return (
            <BoxContainer xs={24} md={12}>
                <BoxWrapper type="flex">
                    <ImageWrapper sm={12} md={10}>
                        <StyledIcon iconUrl={icon} />
                    </ImageWrapper>
                    {this.state.areInstructionsOpen ? (
                        <InstructionsWrapper>
                            <InstructionsHeading>{title}</InstructionsHeading>
                            <RelaxationInstructions />
                            <Link onClick={this.handleToggleInstructions}>Hide instructions</Link>
                        </InstructionsWrapper>
                    ) : (
                        <MusicPlayerWrapper display="flex" sm={12} md={14}>
                            <MusicPlayer
                                color={color.turquoise}
                                track={track}
                                onTrackEnd={this.handleTrackFinished}
                                hideTotalLength
                                buttonOnly
                            />
                            <Title>{title}</Title>
                            {this.props.showMeditationsLink && (
                                <Link onClick={this.goToMeditations}>More meditations</Link>
                            )}
                            {instructions && (
                                <Link onClick={this.handleToggleInstructions}>
                                    Show instructions
                                </Link>
                            )}
                        </MusicPlayerWrapper>
                    )}
                </BoxWrapper>
            </BoxContainer>
        );
    }
}

export const RelaxationStrategiesBox = connect<null, ActionsProps>(null, {
    redirectToUrl,
    setRelaxationRecordingListened,
})(RelaxationStrategiesBoxContainer);
